/**
 * Shared styles for Shadow DOM components
 *
 * Contains:
 * - CSS custom properties (design tokens)
 * - Shared animations (@keyframes)
 * - Common utility styles
 *
 * Import and spread into component's static styles array:
 * static styles = [sharedStyles, css`...`];
 */

import { css } from 'lit';

/**
 * Design tokens - CSS custom properties
 * These are defined on :host of wizard-form and pierce Shadow DOM boundaries
 */
export const designTokens = css`
  /* Colors */
  --wf-color-primary: #8040f0;
  --wf-color-primary-border: #602cbb;
  --wf-color-primary-light: rgba(128, 64, 240, 0.08);
  --wf-color-surface: #f3f3f5;
  --wf-color-surface-hover: #e9e9eb;
  --wf-color-border: #d4d4d4;
  --wf-color-text: #0a0a0a;
  --wf-color-text-secondary: #404040;
  --wf-color-text-muted: #646464;
  --wf-color-error: #dc3545;
  --wf-color-error-light: rgba(220, 53, 69, 0.1);
  --wf-color-badge-bg: #fcfcfc;
  --wf-color-badge-border: #d4d4d4;
  --wf-color-badge-text: #5f5f5f;
  --wf-color-progress-active: rgba(136, 66, 240, 0.6);
  --wf-color-progress-inactive: rgba(255, 255, 255, 0.6);

  /* Focus ring */
  --wf-focus-ring-width: 3px;
  --wf-focus-ring-primary: rgba(128, 64, 240, 0.2);
  --wf-focus-ring-error: rgba(220, 53, 69, 0.2);

  /* Spacing scale */
  --wf-spacing-05: 2px;
  --wf-spacing-1: 4px;
  --wf-spacing-2: 8px;
  --wf-spacing-3: 12px;
  --wf-spacing-4: 16px;
  --wf-spacing-5: 20px;
  --wf-spacing-6: 24px;
  --wf-spacing-8: 32px;

  /* Border radius */
  --wf-radius-sm: 4px;
  --wf-radius-md: 8px;
  --wf-radius-lg: 8px;
  --wf-radius-full: 99px;

  /* Typography */
  --wf-font-size-xs: 0.75rem;
  --wf-font-size-sm: 0.875rem;
  --wf-font-size-base: 1rem;
  --wf-font-size-lg: 1.25rem;
  --wf-font-size-xl: 1.5rem;
  --wf-font-size-2xl: 2rem;
  --wf-font-size-3xl: 2.5rem;
  --wf-font-family-mono: ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace;
  --wf-font-weight-input: 400;
  --wf-font-weight-label: 500;
  --wf-font-weight-heading: 600;
  --wf-font-weight-button: 400;

  /* Input dimensions */
  --wf-input-min-height: 56px;
  --wf-textarea-min-height: 100px;

  /* Component dimensions */
  --wf-btn-min-height: 48px;
  --wf-badge-size: 24px;
  --wf-spinner-size: 20px;
  --wf-progress-height: 4px;

  /* Frosted glass effect (iOS-style) */
  --wf-glass-bg: rgba(255, 255, 255, 0.7);
  --wf-glass-bg-hover: rgba(255, 255, 255, 0.85);
  --wf-glass-border: rgba(0, 0, 0, 0.1);
  --wf-glass-blur: 20px;
  --wf-glass-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
`;

/**
 * Design tokens wrapped in :host for standalone components
 * Use this in components that may be rendered outside wizard-form
 */
export const hostTokens = css`
  :host {
    ${designTokens}
  }
`;

/**
 * Dark theme token overrides
 */
export const darkThemeTokens = css`
  --wf-color-surface: #2d2d2d;
  --wf-color-surface-hover: #3d3d3d;
  --wf-color-border: #4d4d4d;
  --wf-color-text: #f8f9fa;
  --wf-color-text-secondary: #d0d0d0;
  --wf-color-text-muted: #adb5bd;
  --wf-color-badge-bg: #3d3d3d;
  --wf-color-badge-border: #4d4d4d;
  --wf-color-badge-text: #adb5bd;
  --wf-color-progress-active: rgba(200, 200, 200, 0.6);
  --wf-color-progress-inactive: rgba(100, 100, 100, 0.6);

  /* Dark theme frosted glass */
  --wf-glass-bg: rgba(45, 45, 45, 0.7);
  --wf-glass-bg-hover: rgba(60, 60, 60, 0.8);
  --wf-glass-border: rgba(255, 255, 255, 0.1);
`;

/**
 * Shared animations used across components
 */
export const sharedAnimations = css`
  @keyframes wf-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes wf-blink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  @keyframes wf-stepFadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes wf-stepFadeOut {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-10px);
    }
  }

  @keyframes wf-stepSlideInRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes wf-stepSlideInLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

/**
 * Common button base styles
 */
export const buttonBaseStyles = css`
  .wf-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--wf-spacing-2);
    padding: var(--wf-spacing-3) var(--wf-spacing-4);
    min-height: var(--wf-btn-min-height);
    font-size: var(--wf-font-size-base);
    font-weight: var(--wf-font-weight-button);
    font-family: inherit;
    border-radius: var(--wf-radius-md);
    cursor: pointer;
    transition: all 150ms ease;
    border: 1px solid transparent;
    outline: none;
    box-sizing: border-box;
  }

  .wf-btn:focus-visible {
    box-shadow: 0 0 0 var(--wf-focus-ring-width) var(--wf-focus-ring-primary);
  }

  .wf-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * Glass morphism styles (frosted glass effect)
 */
export const glassStyles = css`
  .wf-glass {
    background: var(--wf-glass-bg);
    backdrop-filter: blur(var(--wf-glass-blur));
    -webkit-backdrop-filter: blur(var(--wf-glass-blur));
    border: 1px solid var(--wf-glass-border);
    box-shadow: var(--wf-glass-shadow);
  }

  .wf-glass:hover {
    background: var(--wf-glass-bg-hover);
  }
`;

/* Note: Input base styles (.wf-field-container, .wf-label, .wf-input, .wf-error-message, etc.)
   are now in Shadow DOM - see src/components/base/form-input-base.styles.ts */

/**
 * Default shared styles to include in all components
 * Contains animations and resets
 */
export const sharedStyles = css`
  ${sharedAnimations}

  * {
    box-sizing: border-box;
  }
`;
